<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import PhoneNumber from "@/components/phones/PhoneNumber.vue";
import LanguageTag from "@/components/statuses/LanguageTag.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import CancelledChip from "@/components/chips/CancelledChip.vue";
import TruckRoutesDialog from "@/components/truck/TruckRoutesDialog.vue";
import TruckLink from "@/components/links/TruckLink.vue";
import RouteInfoTravelOrder from "@/views/trips/view/components/RouteInfoTravelOrder.vue";
import RouteMenu from "@/views/trips/view/components/RouteMenu.vue";
import ReplaceTruckDialog from "@/views/trips/view/components/dialogs/ReplaceTruckDialog.vue";
import RateConfirmationDialog from "@/views/trips/view/components/dialogs/RateConfirmationDialog.vue";
import SealsHandlerDialog from "@/views/trips/components/dialogs/SealsHandlerDialog.vue";
import { useFilters } from "@/composables/useFilters";
import { useTruck } from "@/composables/useTruck";
import { RouteStatus, RouteStopStatus } from "@/constants/trip";
import type { TripOverviewRoute } from "@/api/trips/dto/trip.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { SealDto } from "@/api/trips/dto/seal.dto";

const { t } = useI18n();
const { round } = useFilters();
const { truckRoutes, loadTrucksRoutes } = useTruck();

const props = defineProps<{
  route: TripOverviewRoute;
  tripId: number;
}>();

const emit = defineEmits<{
  change: [];
}>();

const expanded = ref<boolean>(true);
const showReplaceTruckDialog = ref<boolean>(false);
const showRateConfirmationDialog = ref<boolean>(false);
const replacedTruck = ref<TruckDto | null>(null);
const assignedTruck = ref<TruckDto | null>(null);
const showTrucksRoutesDialog = ref<boolean>(false);
const showSealsDialog = ref<boolean>(false);

const driverRate = computed<number>(() => {
  return props.route.travelOrders.reduce((sum, travelOrder) => {
    return sum + (round(travelOrder.rate ?? 0) || (travelOrder.dryRunRate ?? 0));
  }, 0);
});

const isPlanned = computed<boolean>(() => {
  return props.route.status === RouteStatus.planned;
});

const isCancelled = computed<boolean>(() => {
  return props.route.status === RouteStatus.cancelled;
});

const isDispatchConfirmed = computed<boolean>(() => {
  return Boolean(props.route.dispatchConfirmed);
});

const routeInfoClass = computed<string>(() => {
  if (isCancelled.value) {
    return "bg-red-lighten-5";
  }

  return "bg-white";
});

const seals = computed<SealDto[]>(() => {
  return props.route.travelOrders.flatMap((travelOrder) => travelOrder.seals ?? []);
});

const hasLoadedPickUp = computed(() => {
  const loadedStatuses = [
    RouteStopStatus.waitingForVerification,
    RouteStopStatus.waitingForGoodToGo,
    RouteStopStatus.completed,
  ];

  return props.route.routeStops.some((stop) => loadedStatuses.includes(stop.status));
});

function toggleExpanded(): void {
  expanded.value = !expanded.value;
}

async function finalizeReplaceTruck(replaced: TruckDto, assigned: TruckDto): Promise<void> {
  emit("change");

  replacedTruck.value = replaced;
  assignedTruck.value = assigned;

  if (!replaced.id || !assigned.id) {
    return;
  }

  await loadTrucksRoutes([replaced.id, assigned.id]);

  showTrucksRoutesDialog.value = truckRoutes.value.some((route) => route.wrongDeadheadDistance);
}
</script>

<template>
  <div :class="routeInfoClass" class="rounded pa-4 mb-4">
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center">
        <div class="font-weight-500 text-grey-darken-2">Route</div>
        <v-chip v-if="isPlanned" class="bg-indigo-lighten-2 ml-3" density="comfortable" size="small">Planned</v-chip>
        <cancelled-chip v-if="isCancelled" class="ml-3" />
      </div>

      <route-menu
        v-model:show-rate-confirmation-dialog="showRateConfirmationDialog"
        v-model:show-replace-truck-dialog="showReplaceTruckDialog"
        :dispatch-confirmed="isDispatchConfirmed"
      />
    </div>

    <div v-if="props.route.truck" class="rounded pa-4 bg-grey-lighten-4 position-relative">
      <div :class="{ expanded }" class="expand-button" @click="toggleExpanded">
        <v-icon color="primary" icon="mdi-chevron-down" size="16"></v-icon>
      </div>
      <div class="d-flex align-center justify-space-between mr-4">
        <div class="d-flex align-center">
          <router-link
            :to="{ name: 'truck-view', params: { id: props.route.truck.id } }"
            class="text-primary text-decoration-none font-weight-500"
            data-qa="route-truck-number"
            target="_blank"
          >
            Truck {{ props.route.truck.number }}
          </router-link>

          <v-btn
            v-if="hasLoadedPickUp"
            class="ml-3 px-2"
            color="primary"
            data-qa="route-seals-button"
            density="compact"
            flat
            prepend-icon="mdi-lock-outline"
            variant="outlined"
            @click="showSealsDialog = true"
          >
            <span>Seals ({{ seals.length }})</span>
          </v-btn>
        </div>
        <div class="font-weight-500 font-size-12 ml-3 text-grey-darken-2" data-qa="route-total-driver-rate">
          Total driver rate: {{ driverRate }}$
        </div>
      </div>
      <div v-if="expanded">
        <div v-if="props.route.driver" class="d-flex align-center mt-3 pb-4 border-b">
          <div
            class="rounded-circle text-uppercase text-white driver-label bg-indigo-lighten-2"
            data-qa="route-driver-label"
          >
            dr
          </div>
          <router-link
            :to="{ name: 'user-view', params: { id: props.route.driver.id } }"
            class="text-primary text-decoration-none ml-2"
            data-qa="route-driver-name"
            target="_blank"
          >
            {{ props.route.driver.name }}
          </router-link>
          <phone-number
            :number="props.route.driver.phone"
            class="text-primary ml-2"
            data-qa="route-driver-phone"
          ></phone-number>
          <language-tag :language="props.route.driver.language" class="ml-3" light></language-tag>
        </div>
        <div v-for="travelOrder in props.route.travelOrders" :key="`${travelOrder.id}-${props.route.id}`" class="mt-4">
          <route-info-travel-order v-if="travelOrder.order" :travel-order="travelOrder" />
        </div>
      </div>
    </div>

    <alert-message
      v-if="props.route.technicalPartial"
      bg-color="indigo-lighten-5"
      border-color="none"
      class="mt-3"
      icon="mdi-information-outline"
      icon-color="indigo"
    >
      <span class="text-indigo font-weight-500"> This route will be processed as Technical Partial </span>
    </alert-message>

    <alert-message v-if="isPlanned" bg-color="indigo-lighten-5" border-color="none" class="mt-3" icon-color="indigo">
      <span class="text-indigo font-weight-500">
        This route is planned and will automatically become active once the truck completes all of previous routes
      </span>
    </alert-message>

    <replace-truck-dialog
      v-if="showReplaceTruckDialog && props.route.truck && props.route.id"
      v-model="showReplaceTruckDialog"
      :current-route-id="props.route.id"
      :current-route-status="props.route.status"
      :replaced-truck="props.route.truck"
      :trip-id="props.tripId"
      @success="finalizeReplaceTruck"
    />

    <truck-routes-dialog
      v-if="showTrucksRoutesDialog && replacedTruck && assignedTruck"
      v-model="showTrucksRoutesDialog"
      :routes="truckRoutes"
    >
      <template #header>
        <div class="text-center">
          <div class="d-flex align-center justify-center text-center font-size-20 font-weight-500 col-gap-1">
            <span>{{ t("general.route-has-been-updated") }}</span>
          </div>

          <div class="d-flex align-center justify-center font-size-16 mt-4">
            <truck-link :truck-id="replacedTruck.id" :truck-number="replacedTruck.number" class="mr-1" />
            <span class="text-grey-darken-2 font-weight-500"
              >{{ t("general.was-unassigned-from-route") }}{{ props.route.id }}</span
            >
          </div>

          <div class="d-flex align-center justify-center font-size-16 mt-1">
            <truck-link :truck-id="assignedTruck.id" :truck-number="assignedTruck.number" class="mr-1" />
            <span class="text-grey-darken-2 font-weight-500"
              >{{ t("general.was-assigned-to-route") }}{{ props.route.id }}</span
            >
          </div>

          <p class="text-grey-darken-1 mt-4">
            {{ t("general.it_affected_empty_miles") }}
            <br />
            <span>{{ t("general.please-recalculate-or-cancel") }}</span>
          </p>
        </div>
      </template>
    </truck-routes-dialog>

    <seals-handler-dialog
      v-if="showSealsDialog"
      v-model="showSealsDialog"
      :route-stops="props.route.routeStops"
      :travel-orders="props.route.travelOrders"
      :trip-id="props.tripId"
      @change="emit('change')"
    />

    <rate-confirmation-dialog v-if="props.route.id" v-model="showRateConfirmationDialog" :route-id="props.route.id" />
  </div>
</template>

<style lang="scss" scoped>
.expand-button {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  transition: all 250ms ease-in-out;

  &.expanded {
    transform: rotate(180deg);
  }
}

.driver-label {
  font-size: 9px;
  font-weight: 600;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
}
</style>
